import * as React from 'react';
import {Button, Table} from 'antd';
import {ErrorAlert} from '../common/ErrorAlert';
import {ColumnFilterItem, ColumnProps} from 'antd/lib/table';
import {ErrorResponse, GetCategoriesResponse, GridRequest, Item, PageResponseEnvelope} from '../api/dtos/dtos';
import furoApi from '../api/FuroApi';
import * as paths from '../paths';
import {RouteComponentProps, withRouter} from 'react-router';
import {Link} from 'react-router-dom';
import locales from '../locales';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import * as tableUtils from '../utils/table-utils';
import {filterableColumn, GridState, searchableSortableColumn} from '../utils/table-utils';
import * as routerUtils from '../utils/router-utils';
import {getItemStateFilters, renderStateElementCondensed} from '../common/state';
import ButtonBar from '../common/ButtonBar';
import MaintenanceSheetFilterSelector from "./MaintenanceSheetFilterSelector";
import {FeatureFilter} from "../common/FeatureFilter";
import {renderItemName} from "./item-name-renderer";
import accountProvider from "../account/AccountProvider";
import {renderItemProperties} from "./item-property-renderer";

interface State extends GridState<Item> {
  categoryFilters: ColumnFilterItem[];
  maintenanceSheetFilter: FeatureFilter;
  error?: ErrorResponse;
}

class ItemsPage extends React.Component<RouteComponentProps, State> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  public state: State = {
    // Default initial grid state
    ...tableUtils.initialGridState(),

    // Initial sorting
    sortColumnKey: 'name',
    sortOrder: 'ascend',

    maintenanceSheetFilter: FeatureFilter.ALL,

    // Override with cached state from Router
    ...this.props.location.state,

    categoryFilters: [],
  };

  public componentDidMount() {
    this.loadCategories();
    this.reload();
  }

  public render() {
    const {data, error, categoryFilters} = this.state;
    const addButtonVisible = accountProvider.isAdmin() || accountProvider.isSales();
    const columns: Array<ColumnProps<Item>> = [
      {
        ...filterableColumn('', 'state', this.state, r => renderStateElementCondensed(r.state), getItemStateFilters(), false),
        width: 38,
      },
      searchableSortableColumn(locales.appellation, 'name', this.state, r => <Link
        to={paths.viewItemPath(r.id)}>{renderItemName(r)}</Link>),
      searchableSortableColumn(locales.inventoryNumber, 'inventoryNumber', this.state, r => r.inventoryNumber),
      filterableColumn(locales.category, 'category', this.state, r => r.category, categoryFilters),
      searchableSortableColumn(locales.resource, 'resource', this.state, r => r.resource),
      searchableSortableColumn(locales.properties, 'details', this.state, renderItemProperties),
      searchableSortableColumn(locales.comment, 'comment', this.state, r => r.comment),
    ];

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <ButtonBar style={{marginBottom: 8}}>
          <MaintenanceSheetFilterSelector
            value={this.state.maintenanceSheetFilter}
            onChange={this.onMaintenanceSheetFilterChange}
          />
          {addButtonVisible &&
              <Button size="small" type="primary" icon="plus"
                      onClick={this.onAddButtonClick}>{locales.add}</Button>
          }
        </ButtonBar>

        <Table
          dataSource={data}
          columns={columns}
          rowKey={tableUtils.idRowKeyFn}
          bordered={true}
          size="small"
          pagination={tableUtils.defaultPaginationConfig(this.state)}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
          scroll={{x: 1024}}
        />

        {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
      </>
    );
  }

  private onMaintenanceSheetFilterChange = (filter: FeatureFilter) => {
    this.setState({maintenanceSheetFilter: filter}, () => this.reload());
  };

  private onGridChange = (newState: GridState<Item>) => {
    this.setState({...newState}, () => this.reload());
  };

  private onAddButtonClick = () => {
    this.props.history.push(paths.createItemPath)
  };

  private loadCategories = () => {
    const promise = furoApi.items.getCategories()
      .then(this.onLoadCategoriesSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onLoadCategoriesSuccess = (response: GetCategoriesResponse) => {
    this.setState({
      categoryFilters: response.categories.map(c => ({text: c, value: c}))
    });
  };

  private reload = () => {
    const request = tableUtils.toGridRequest(this.state);
    this.addExtraFiltersToGridRequest(request);

    const promise = furoApi.items.getAll(request)
      .then(this.onReloadSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private addExtraFiltersToGridRequest(request: GridRequest) {
    request.filters = {
      ...request.filters,
      'maintenance_sheet': [this.state.maintenanceSheetFilter]
    };
  }

  private onReloadSuccess = (response: PageResponseEnvelope<Item>) => {
    this.setState({
      ...tableUtils.toGridState(response),
      error: undefined,
    }, () => {
      routerUtils.cacheComponentStateForLocation(this.props, this.state);
    });
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error})
  };
}

export default withRouter(ItemsPage);


// WEBPACK FOOTER //
// ./src/item/ItemsPage.tsx